import { ReactElement } from "react";

import { FitnessGoals } from "@/constants/fitnessGoal";
import { FitnessLevels } from "@/constants/fitnessLevels";

export const getCalisthenicTextByPrimaryGoal: {
    [key in FitnessGoals]?: { title: string; description: ReactElement };
} = {
    [FitnessGoals.GainMuscle]: {
        title: "Build muscle without weights!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    For those aiming to pack on lean muscle,{" "}
                    <span className="font-bold">calisthenics</span> offers a powerful solution.
                </div>
                <br />
                <div>Forget about expensive gym memberships or bulky equipment.</div>
                <br />
                <div>
                    Challenging bodyweight exercises that stimulate <b>muscle growth</b> are the
                    cornerstone of your customized plan!
                </div>
            </div>
        ),
    },
    [FitnessGoals.GetInShape]: {
        title: "Transform your body, boost your fitness!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    For individuals seeking overall fitness improvement,{" "}
                    <span className="font-bold">calisthenics</span> delivers remarkable results.
                </div>
                <br />
                <div>
                    Say goodbye to complicated workout routines and hello to efficient, full-body
                    training.
                </div>
                <br />
                <div>
                    Dynamic exercises that{" "}
                    <span className="font-bold"> enhance strength, flexibility, and endurance</span>{" "}
                    are at the core of your tailored fitness journey!
                </div>
            </div>
        ),
    },
    [FitnessGoals.LoseWeight]: {
        title: "Weight loss in weeks!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    <span className="font-bold">Calisthenics</span> is where the results truly shine
                    for those looking to <span className="font-bold"> get lean quickly</span>.
                </div>
                <br />
                <div>No need to spend hours on a treadmill or lift heavy weights.</div>
                <br />
                <div>
                    Quick bodyweight workouts that put you in{" "}
                    <span className="font-bold">fat-burning mode</span> are waiting for you in your
                    personalized plan!
                </div>
            </div>
        ),
    },
    [FitnessGoals.BeHealthy]: {
        title: "Unlock your body's potential for wellness!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    For those prioritizing long-term health, calisthenics is the key to a vibrant
                    lifestyle.
                </div>
                <br />
                <div>No more fad diets or extreme workout regimens.</div>
                <br />
                <div>
                    Balanced, sustainable bodyweight exercises that{" "}
                    <span className="font-bold">promote holistic health</span> are the foundation of
                    your personalized plan!
                </div>
            </div>
        ),
    },
    [FitnessGoals.GetActive]: {
        title: "Energize your life through movement!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    For individuals ready to break free from sedentary habits,{" "}
                    <span className="font-bold">calisthenics</span> offers an exciting path to an
                    active lifestyle.
                </div>
                <br />
                <div>Forget about intimidating gym environments or complex equipment.</div>
                <br />
                <div>
                    Fun, accessible bodyweight movements that{" "}
                    <span className="font-bold">get you moving and feeling great</span> are the
                    focus of your personalized plan!
                </div>
            </div>
        ),
    },
};

export const getCalisthenicsTextByFitnessLevel = {
    [FitnessLevels.Beginner]: {
        title: "Calisthenics yields significant physical improvements, even for beginners.",
        description: (
            <div className="text-zing-12-18 text-zing-neutral-cool-grey-400 female:text-black">
                *According to research by the Sport and Exercise Sciences Research Unit, University
                of Palermo and Department of Biomedical Sciences, University of Padova
            </div>
        ),
    },
    [FitnessLevels.Intermediate]: {
        title: "Calisthenics workouts enhance strength and body composition in individuals with intermediate fitness level.",
        description: (
            <div className="text-zing-12-18 text-zing-neutral-cool-grey-400 female:text-black">
                *According to research by the Sport and Exercise Sciences Research Unit, University
                of Palermo and Department of Biomedical Sciences, University of Padova
            </div>
        ),
    },
    [FitnessLevels.Advanced]: {
        title: "Calisthenics workouts offer measurable benefits, even for regularly exercising individuals.",
        description: (
            <div className="text-zing-12-18 text-zing-neutral-cool-grey-400 female:text-black">
                *According to research by the Sport and Exercise Sciences Research Unit, University
                of Palermo and Department of Biomedical Sciences, University of Padova
            </div>
        ),
    },
};

export const getCalisthenicsTextByTrainingFrequency = {
    hardly_ever: {
        title: "It's never too late!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    Whether you're a beginner or simply don't have enough time for exercise,{" "}
                    <span className="font-bold">
                        calisthenics is designed for all fitness levels{" "}
                    </span>{" "}
                    and <span className="font-bold">schedules</span>.
                </div>
            </div>
        ),
    },
    "1_2_per_week": {
        title: "On the right track!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    <div>Your consistent effort sets you apart.</div>
                    <br />
                    <div>
                        With a <span className="font-bold">personalized calisthenics plan, </span>
                        you'll be able to{" "}
                        <span className="font-bold">
                            stay consistent, boost muscle growth, and make steady progress!
                        </span>
                    </div>
                </div>
            </div>
        ),
    },
    "3_4_per_week": {
        title: "On the right track!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    <div>Your consistent effort sets you apart.</div>
                    <br />
                    <div>
                        With a <span className="font-bold">personalized calisthenics plan, </span>
                        you'll be able to{" "}
                        <span className="font-bold">
                            stay consistent, boost muscle growth, and make steady progress!
                        </span>
                    </div>
                </div>
            </div>
        ),
    },
    "5_6_per_week": {
        title: "Impressive consistency!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    <div>You're exercising more than 93% of users.</div>
                    <br />
                    <div>
                        Adding <span className="font-bold">calisthenics plan</span> to your workout
                        plan can break you out of a rut and help you{" "}
                        <span className="font-bold">achieve new levels of fitness</span>
                        you didn't even know were within your reach!
                    </div>
                </div>
            </div>
        ),
    },
    everyday: {
        title: "Impressive consistency!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    <div>You're exercising more than 93% of users.</div>
                    <br />
                    <div>
                        Adding <span className="font-bold">calisthenics plan</span> to your workout
                        plan can break you out of a rut and help you{" "}
                        <span className="font-bold">achieve new levels of fitness</span>
                        you didn't even know were within your reach!
                    </div>
                </div>
            </div>
        ),
    },
};

export const getCalisthenicsTextByObstacle = {
    consistencyLack: {
        title: "Program created by experts!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    Your <span className="font-bold">calisthenics </span> plan is being{" "}
                    <span className="font-bold">designed by experts</span> in the field who helped
                    thousands of people just like you get stronger and reach their fitness goals.
                </div>
            </div>
        ),
    },
    timeConstraints: {
        title: "Workouts for your fitness level!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    Your <span className="font-bold">calisthenics </span> plan is tailored to your
                    fitness level, your goal physique, and even your busy schedule.
                </div>
                <br />
                <div>Achieve your fitness goals with quick bodyweight workouts.</div>
            </div>
        ),
    },
    lackOfMotivation: {
        title: "Lack of motivation is very common but...",
        description: (
            <div className="text-zing-16-22">
                <div>
                    Your personalized <span className="font-bold">calisthenics</span> plan is
                    designed for steady progress.
                </div>
                <br />
                <div>
                    You'll get{" "}
                    <span className="font-bold">
                        gentle reminders, handy tracking tools and progress reports
                    </span>{" "}
                    to keep your motivation high until you reach your goal.
                </div>
            </div>
        ),
    },
    fatigue: {
        title: "Workouts for your fitness level!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    Your <span className="font-bold">calisthenics </span> plan is tailored to your
                    fitness level, your goal physique, and even your busy schedule.
                </div>
                <br />
                <div>Achieve your fitness goals with quick bodyweight workouts.</div>
            </div>
        ),
    },
    lackOfVisibleProgress: {
        title: "Program created by experts!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    Your <span className="font-bold">calisthenics </span> plan is being{" "}
                    <span className="font-bold">designed by experts</span> in the field who helped
                    thousands of people just like you get stronger and reach their fitness goals.
                </div>
            </div>
        ),
    },
    plateausInProgress: {
        title: "Zing Coach has got your back!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    You'll get a personalized step-by-step{" "}
                    <span className="font-bold">calisthenics</span> plan and full support from a
                    team of experts.
                </div>
                <br />
                <div>
                    We're here to help you{" "}
                    <span className="font-bold">create the body you deserve.</span>
                </div>
            </div>
        ),
    },
    lackOfSupport: {
        title: "Program created by experts!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    Your <span className="font-bold">calisthenics </span> plan is being{" "}
                    <span className="font-bold">designed by experts</span> in the field who helped
                    thousands of people just like you get stronger and reach their fitness goals.
                </div>
            </div>
        ),
    },
    injuriesOrPain: {
        title: "Zing Coach has got your back!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    You'll get a personalized step-by-step{" "}
                    <span className="font-bold">calisthenics</span> plan and full support from a
                    team of experts.
                </div>
                <br />
                <div>
                    We're here to help you{" "}
                    <span className="font-bold">create the body you deserve.</span>
                </div>
            </div>
        ),
    },
};

export const getCalisthenicsTextByHealthRestrictions = (healthProblems: string[] = []) => {
    if (healthProblems.length > 1) {
        return {
            title: "Calisthenics is the answer!",
            description: (
                <div className="text-zing-16-22">
                    <div>
                        <div>
                            Your personalized <span className="font-bold">calisthenics</span> plan
                            includes exercises that bring{" "}
                            <span className="font-bold">results instead of injuries.</span>
                        </div>
                        <br />
                        <div>
                            Get stronger and fitter with{" "}
                            <span className="font-bold">a plan that puts your safety first.</span>
                        </div>
                    </div>
                </div>
            ),
        };
    } else if (healthProblems.includes("none")) {
        return {
            title: "More strength, less pain, no injuries!",
            description: (
                <div className="text-zing-16-22">
                    <div>
                        <div>
                            <span className="font-bold">Calisthenics</span> is designed to help you{" "}
                            <span className="font-bold">strengthen your body</span> and{" "}
                            <span className="font-bold"> prevent injuries</span> without lifting
                            weights or buying any extra equipment.
                        </div>
                    </div>
                </div>
            ),
        };
    } else if (healthProblems.includes("backPain")) {
        return {
            title: "More strength, less back pain!",
            description: (
                <div className="text-zing-16-22">
                    <div>
                        <div>
                            <span className="font-bold">Calisthenics</span> is designed to help you{" "}
                            <span className="font-bold">strengthen your lower back</span> and{" "}
                            <span className="font-bold">improve your spine health</span> without
                            lifting weights or buying any extra equipment.
                        </div>
                    </div>
                </div>
            ),
        };
    }
    const healthFriendly = healthProblems.map((el) => el.replace("Pain", "-friendly")).join(", ");
    return {
        title: "Calisthenics is the answer!",
        description: (
            <div className="text-zing-16-22">
                <div>
                    <div>
                        Your personalized calisthenics plan includes low-impact,{" "}
                        <span className="font-bold">{healthFriendly}</span> exercises that bring
                        results instead of injuries.
                    </div>
                    <br />
                    <div>
                        Get stronger and fitter with{" "}
                        <span className="font-bold">a plan that puts your safety first.</span>
                    </div>
                </div>
            </div>
        ),
    };
};
